import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUpload as Upload } from 'react-icons/fa';
import { IoClose as X, IoHelpCircle as HelpIcon, IoCheckmarkCircle as CheckIcon } from 'react-icons/io5';

export default function GridTool({
  gridLayouts,
  gridSettings,
  changeGridLayout,
  changeGridDimensions,
  updateGridSetting,
  addImageToGrid,
  removeGridImage,
  processImage,
  isProcessing,
  showToolTip,
  gridFileInputRef,
  quality,
  setQuality
}) {
  const { t } = useTranslation();
  const layoutConfig = gridLayouts.find((l) => l.id === gridSettings.layout);
  const maxImages =
    layoutConfig?.areas?.length || gridSettings.customRows * gridSettings.customCols;

  return (
    <div className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-500 overflow-hidden border border-gray-100">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">{t('grid.title')}</h3>
          <button
            onMouseEnter={(e) => showToolTip(t('grid.tooltip'), e)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <HelpIcon size={16} />
          </button>
        </div>

        <div>
          <p className="text-gray-700 mb-4">{t('grid.chooseLayout')}</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mb-6">
            {gridLayouts.map((layout) => (
              <button
                key={layout.id}
                onClick={() => changeGridLayout(layout.id)}
                className={`p-3 flex flex-col items-center justify-center rounded-lg border transition-all duration-300 hover:shadow-sm transform hover:-translate-y-1 ${
                  gridSettings.layout === layout.id
                    ? 'border-blue-600 bg-blue-50 shadow-sm ring-1 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                aria-label={`${t(`grid.layout.${layout.id}`, { defaultValue: layout.name })}${t('grid.layout.suffix')}`}
              >
                <div
                  className="w-14 h-14 mb-2 relative bg-gray-50 rounded"
                  style={{
                    display: layout.isHeterogeneous ? 'flex' : 'grid',
                    gridTemplateRows: layout.isHeterogeneous ? 'none' : `repeat(${layout.rows}, 1fr)`,
                    gridTemplateColumns: layout.isHeterogeneous ? 'none' : `repeat(${layout.cols}, 1fr)`
                  }}
                >
                  {layout.isHeterogeneous ? (
                    <>
                      {layout.id === 'hetero1' && (
                        <>
                          <div className="absolute top-0 left-0 w-1/2 h-2/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute top-0 right-0 w-1/2 h-2/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute bottom-0 left-0 w-full h-3/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                        </>
                      )}
                      {layout.id === 'hetero2' && (
                        <>
                          <div className="absolute top-0 left-0 w-3/5 h-1/2 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute bottom-0 left-0 w-3/5 h-1/2 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute top-0 right-0 w-2/5 h-full border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                        </>
                      )}
                      {layout.id === 'hetero3' && (
                        <>
                          <div className="absolute top-0 left-0 w-full h-3/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute bottom-0 left-0 w-1/2 h-2/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute bottom-0 right-0 w-1/2 h-2/5 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                        </>
                      )}
                      {layout.id === 'hetero4' && (
                        <>
                          <div className="absolute top-0 left-0 w-2/5 h-full border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute top-0 right-0 w-3/5 h-1/2 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                          <div className="absolute bottom-0 right-0 w-3/5 h-1/2 border border-gray-300" style={{ backgroundColor: '#e0f2fe' }}></div>
                        </>
                      )}
                    </>
                  ) : (
                    Array.from({ length: layout.rows * layout.cols }).map((_, i) => (
                      <div
                        key={i}
                        className="border border-gray-300"
                        style={{ backgroundColor: i < gridSettings.images.length ? '#e0f2fe' : 'transparent' }}
                      />
                    ))
                  )}
                </div>
                <span className="text-xs font-medium">{t(`grid.layout.${layout.id}`, { defaultValue: layout.icon })}</span>
              </button>
            ))}
          </div>

          {!layoutConfig?.isHeterogeneous && (
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.rows')}</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={gridSettings.customRows}
                  onChange={(e) => changeGridDimensions(Number(e.target.value), gridSettings.customCols)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  aria-label={t('grid.rows.aria')}
                />
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.cols')}</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={gridSettings.customCols}
                  onChange={(e) => changeGridDimensions(gridSettings.customRows, Number(e.target.value))}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  aria-label={t('grid.cols.aria')}
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded-xl">
          <h4 className="font-medium text-gray-800 mb-4">{t('grid.advanced.title')}</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.fitMode.label')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateGridSetting('fitMode', 'cover')}
                  className={`p-3 border rounded-lg text-center transition-all duration-300 ${
                    gridSettings.fitMode === 'cover' ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span>{t('grid.fitMode.cover.title')}</span>
                    {gridSettings.fitMode === 'cover' && <CheckIcon size={16} className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-gray-500">{t('grid.fitMode.cover.desc')}</p>
                </button>

                <button
                  onClick={() => updateGridSetting('fitMode', 'contain')}
                  className={`p-3 border rounded-lg text-center transition-all duration-300 ${
                    gridSettings.fitMode === 'contain' ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span>{t('grid.fitMode.contain.title')}</span>
                    {gridSettings.fitMode === 'contain' && <CheckIcon size={16} className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-gray-500">{t('grid.fitMode.contain.desc')}</p>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.spacing.label', { value: gridSettings.spacing })}</label>
              <input
                type="range"
                min="0"
                max="10"
                value={gridSettings.spacing}
                onChange={(e) => updateGridSetting('spacing', Number(e.target.value))}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('grid.spacing.aria')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.canvasSize.label', { value: gridSettings.canvasSize })}</label>
              <input
                type="range"
                min="400"
                max="1600"
                step="100"
                value={gridSettings.canvasSize}
                onChange={(e) => updateGridSetting('canvasSize', Number(e.target.value))}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('grid.canvasSize.aria')}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{t('grid.canvasSize.small')}</span>
                <span>{t('grid.canvasSize.medium')}</span>
                <span>{t('grid.canvasSize.large')}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('grid.quality.label', { value: Math.round(quality * 100) })}</label>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.05"
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                aria-label={t('grid.quality.aria')}
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-gray-700 mb-4">{t('grid.addImages.label', { count: maxImages })}</p>
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 flex flex-wrap gap-4 min-h-[140px] bg-gray-50">
            {gridSettings.images.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image.src}
                  alt={t('grid.image.alt', { index: index + 1 })}
                  className="w-24 h-24 object-cover rounded-lg border shadow-sm transition-transform duration-300 group-hover:scale-105"
                />
                <button
                  onClick={() => removeGridImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-red-300"
                  aria-label={t('grid.removeImage.aria', { index: index + 1 })}
                >
                  <X size={14} />
                </button>
              </div>
            ))}

            {gridSettings.images.length < maxImages && (
              <label
                className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-500 hover:text-blue-600 transition-all duration-300 cursor-pointer group bg-white"
                htmlFor="grid-upload"
              >
                <Upload size={24} className="mb-1 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-medium">{t('grid.addImage.button')}</span>
                <input
                  id="grid-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  ref={gridFileInputRef}
                  onChange={addImageToGrid}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {gridSettings.images.length > 0 && (
          <button
            onClick={() => processImage('grid')}
            disabled={isProcessing || gridSettings.images.length === 0}
            className={`w-full py-3 rounded-lg transition-all duration-300 font-medium ${
              isProcessing || gridSettings.images.length === 0
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow transform hover:-translate-y-0.5'
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t('grid.generating')}
              </div>
            ) : t('grid.generate')
            }
          </button>
        )}
      </div>
    </div>
  );
}